/**
 * Customer Sign-Off form definition + data shape.
 *
 * The employee fills the whole form (and the Company Representative section) first,
 * then the client reviews on the same device and completes their initials + the
 * Client Representative section. The full form is stored in customer_signoffs.form_data
 * and rendered to a PDF that is emailed to the client, billing, and the order creator.
 */

export interface YesNoTask {
  id: string;
  label: string;
  /** When true the task offers a third "N/A" option. */
  allowNa?: boolean;
}

/** Installation Verification checklist. */
export const VERIFICATION_TASKS: YesNoTask[] = [
  { id: 'robots_installed', label: 'Robots installed' },
  { id: 'charging_stations_installed', label: 'Charging stations installed' },
  { id: 'maps_loaded', label: 'Maps loaded and validated' },
  { id: 'workflows_configured', label: 'Workflows configured' },
  { id: 'elevator_integration', label: 'Elevator integration tested (if applicable)', allowNa: true },
  { id: 'automatic_doors', label: 'Automatic doors tested', allowNa: true },
  { id: 'wifi_verified', label: 'Wi-Fi/Network connectivity verified' },
  { id: 'safety_inspection', label: 'Safety inspection completed' },
  { id: 'operational_testing', label: 'Operational testing completed' },
];

export interface TrainingTopic {
  id: string;
  label: string;
}

/** Training Completion topics (each has a done checkbox + trainee initials). */
export const TRAINING_TOPICS: TrainingTopic[] = [
  { id: 'robot_overview', label: 'Robot Overview' },
  { id: 'daily_operation', label: 'Daily Operation' },
  { id: 'bin_install_removal', label: 'Bin Installation & Removal' },
  { id: 'cleaning', label: 'Cleaning Procedures' },
  { id: 'charging', label: 'Charging Procedures' },
  { id: 'safety', label: 'Safety Procedures' },
  { id: 'error_recovery', label: 'Error Recovery / Troubleshooting' },
  { id: 'supervisor_admin', label: 'Supervisor/Admin Functions' },
  { id: 'preventive_maintenance', label: 'Preventive Maintenance' },
  { id: 'questions_answered', label: 'Questions Answered' },
];

export interface AcceptanceItem {
  id: string;
  label: string;
}

/** Client Acceptance Checklist (client-confirmed). */
export const ACCEPTANCE_ITEMS: AcceptanceItem[] = [
  { id: 'equipment_received', label: 'All equipment listed above has been received.' },
  { id: 'no_damage', label: 'Equipment is free from visible damage.' },
  { id: 'install_satisfactory', label: 'Installation has been completed to our satisfaction.' },
  { id: 'functionality_demonstrated', label: 'Robots have demonstrated expected functionality.' },
  { id: 'training_completed', label: 'Required training has been completed.' },
  { id: 'docs_received', label: 'User manuals and documentation have been received.' },
  { id: 'warranty_provided', label: 'Warranty and support information has been provided.' },
  { id: 'support_contact_understood', label: 'We understand how to contact technical support.' },
];

// ── Stored form_data shape ─────────────────────────────────────────────────

export interface SignoffProjectInfo {
  customerName: string;
  siteName: string;
  siteAddress: string;
  deploymentDate: string;
  projectManager: string;
  technicians: string;
}

export interface SignoffEquipmentRow {
  name: string;
  qtyExpected: string;
  qtyReceived: string;
  /** '', 'Good', or 'Damaged'. */
  condition: string;
  clientInitials: string;
}

export interface SignoffTrainingEntry {
  done: boolean;
  initials: string;
}

export interface SignoffPunchListRow {
  item: string;
  responsibleParty: string;
  targetDate: string;
}

export interface SignoffRep {
  name: string;
  title: string;
  /** PNG data URL. */
  signature: string;
  date: string;
}

export interface SignoffFollowUp {
  thirtyDayScheduled: string;
  supportContact: string;
  supportPhone: string;
  supportEmail: string;
}

export interface CustomerSignoffFormData {
  project: SignoffProjectInfo;
  equipment: SignoffEquipmentRow[];
  /** Keyed by VERIFICATION_TASKS id → '', 'Yes', 'No', or 'N/A'. */
  verification: Record<string, string>;
  training: {
    /** Keyed by TRAINING_TOPICS id. */
    topics: Record<string, SignoffTrainingEntry>;
    conductedByName: string;
    conductedByDate: string;
  };
  /** Keyed by ACCEPTANCE_ITEMS id → boolean. */
  acceptance: Record<string, boolean>;
  punchList: SignoffPunchListRow[];
  clientComments: string;
  finalAcceptance: {
    company: SignoffRep;
    client: SignoffRep;
  };
  followUp: SignoffFollowUp;
}

export function emptyRep(): SignoffRep {
  return { name: '', title: '', signature: '', date: '' };
}

/** A blank form_data, used before the employee starts filling it in. */
export function emptyCustomerSignoffForm(): CustomerSignoffFormData {
  return {
    project: {
      customerName: '',
      siteName: '',
      siteAddress: '',
      deploymentDate: '',
      projectManager: '',
      technicians: '',
    },
    equipment: [],
    verification: {},
    training: { topics: {}, conductedByName: '', conductedByDate: '' },
    acceptance: {},
    punchList: [],
    clientComments: '',
    finalAcceptance: { company: emptyRep(), client: emptyRep() },
    followUp: { thirtyDayScheduled: '', supportContact: '', supportPhone: '', supportEmail: '' },
  };
}

/**
 * Merge a possibly-partial stored form_data onto a fresh blank form so every field
 * is defined (older/partial rows won't crash the UI).
 */
export function normalizeCustomerSignoffForm(raw: unknown): CustomerSignoffFormData {
  const base = emptyCustomerSignoffForm();
  if (!raw || typeof raw !== 'object') return base;
  const r = raw as Partial<CustomerSignoffFormData>;
  return {
    project: { ...base.project, ...(r.project ?? {}) },
    equipment: Array.isArray(r.equipment) ? (r.equipment as SignoffEquipmentRow[]) : base.equipment,
    verification: { ...base.verification, ...(r.verification ?? {}) },
    training: {
      topics: { ...(r.training?.topics ?? {}) },
      conductedByName: r.training?.conductedByName ?? '',
      conductedByDate: r.training?.conductedByDate ?? '',
    },
    acceptance: { ...base.acceptance, ...(r.acceptance ?? {}) },
    punchList: Array.isArray(r.punchList) ? (r.punchList as SignoffPunchListRow[]) : base.punchList,
    clientComments: r.clientComments ?? '',
    finalAcceptance: {
      company: { ...emptyRep(), ...(r.finalAcceptance?.company ?? {}) },
      client: { ...emptyRep(), ...(r.finalAcceptance?.client ?? {}) },
    },
    followUp: { ...base.followUp, ...(r.followUp ?? {}) },
  };
}
